// src/components/ProductItem.jsx
import React, { useEffect, useState } from "react"
import { IoClose, IoDuplicateSharp } from "react-icons/io5"
import { Md360, MdHeight } from "react-icons/md"
import { FaChevronDown, FaChevronUp } from "react-icons/fa"
import placeholder from "../assets/placeholder.jpg"

/**
 * Component for displaying a single product with controls and material sections
 */
const ProductItem = ({
  pn,
  selectProductNodeInstance,
  removeProductNodeInstance,
  duplicateProductNode,
  rotateProductNodeInstance,
  changeProductNodeInstanceHeight,
  applyMaterial,
  materials,
  wood,
  labels,
}) => {
  const [materialsOpen, setMaterialsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState("imbottito")

  // Extract properties from the product node
  const productName = pn.matchedLocalProduct
    ? pn.matchedLocalProduct.name
    : pn.refData?.name || "Prodotto"

  const matchedProduct = pn.matchedLocalProduct || {
    hasFabric: pn.configurableMaterials?.some((m) => m.name === "imbottito"),
    hasWood: pn.configurableMaterials?.some((m) => m.name === "struttura"),
    hasPiano: pn.configurableMaterials?.some(
      (m) => m.name === "piano" || m.name === "finitura"
    ),
  }

  // Check if a material type has multiple options
  const hasMultipleOptions = (materialType) => {
    const configurableMaterial = pn.configurableMaterials?.find(
      (m) => m.name === materialType
    )

    return (
      configurableMaterial &&
      configurableMaterial.materialVariations &&
      configurableMaterial.materialVariations.length > 1
    )
  }

  // Get available material types with multiple options
  const availableMaterialTypes = []
  if (matchedProduct.hasFabric && hasMultipleOptions("imbottito")) {
    availableMaterialTypes.push({
      id: "imbottito",
      label: labels.upholstery,
    })
  }
  if (matchedProduct.hasWood && hasMultipleOptions("struttura")) {
    availableMaterialTypes.push({
      id: "struttura",
      label: labels.structure,
    })
  }
  if (
    matchedProduct.hasPiano &&
    (hasMultipleOptions("piano") || hasMultipleOptions("finitura"))
  ) {
    availableMaterialTypes.push({
      id: "finitura",
      label: labels.finish,
    })
  }

  // If there are no material types with multiple options, default to "imbottito"
  useEffect(() => {
    if (
      availableMaterialTypes.length > 0 &&
      !availableMaterialTypes.find((t) => t.id === activeTab)
    ) {
      setActiveTab(availableMaterialTypes[0].id)
    }
  }, [availableMaterialTypes, activeTab])

  // Find the applied materials from configurableMaterials
  const getAppliedMaterial = (materialType) => {
    if (!pn.configurableMaterials) return null

    const material = pn.configurableMaterials.find(
      (m) => m.name === materialType
    )
    if (!material) return null

    // Use activeVariation or appliedVariation (whichever is available)
    const variationName = material.appliedVariation || material.activeVariation
    if (!variationName) return null

    console.log(`[getAppliedMaterial] ${materialType}:`, variationName)

    // Try to find the material in our collections
    const allMaterialItems =
      materialType === "imbottito"
        ? materials.flatMap((m) => m.items || [])
        : wood.flatMap((w) => w.items || [])

    const match = allMaterialItems.find((item) => {
      if (!item) return false
      return item.name === variationName || item.slug === variationName
    })

    if (match) {
      return {
        name: match.name,
        thumbnail: match.thumbnail || placeholder,
      }
    }

    // If the material was found among the variations in configurableMaterials
    // but we don't have a match in our local data, try to find at least the color
    if (
      material.materialVariations &&
      Array.isArray(material.materialVariations)
    ) {
      const variation = material.materialVariations.find(
        (v) => v.name === variationName
      )
      if (variation && variation.color) {
        return {
          name: variationName,
          thumbnail: placeholder,
          color: variation.color, // We can use this to render a color instead of an image
        }
      }
    }

    // If no match, return a basic object
    return {
      name: variationName,
      thumbnail: placeholder,
    }
  }

  const appliedRivestimento = getAppliedMaterial("imbottito")
  const appliedStruttura = getAppliedMaterial("struttura")
  const appliedFinitura =
    getAppliedMaterial("piano") || getAppliedMaterial("finitura")

  // Helper function to create a material thumbnail
  const MaterialThumbnail = ({ material, label }) => {
    if (!material) return null

    return (
      <div className="ay-flex ay-items-center ay-bg-gray-50 ay-py-1 ay-px-2 ay-rounded-full">
        {material.color ? (
          <div
            className="ay-w-6 ay-h-6 ay-rounded-full ay-mr-2 ay-border ay-border-gray-300"
            style={{ backgroundColor: material.color }}
          />
        ) : (
          <img
            src={material.thumbnail}
            alt={material.name}
            className="ay-w-6 ay-h-6 ay-rounded-full ay-object-cover ay-mr-2 ay-border ay-border-gray-300"
          />
        )}
        <span className="ay-text-xs ay-truncate ay-max-w-[100px]">
          {material.name}
        </span>
      </div>
    )
  }
  return (
    <div
      className={`ay-border ay-border-gray-200 ay-rounded-lg ay-mb-6 ay-bg-white ay-shadow-sm ${
        pn.colliding ? "ay-border-red-500" : ""
      }`}
    >
      {/* Product Header */}
      <div className="ay-p-4">
        <div
          className="ay-flex ay-items-center ay-cursor-pointer"
          onClick={() => selectProductNodeInstance(pn)()}
        >
          {/* Product Preview Image */}
          <div
            className="ay-w-24 ay-h-24 ay-bg-contain ay-bg-center ay-bg-no-repeat ay-mr-4 ay-rounded-lg ay-border ay-border-gray-200 ay-flex-shrink-0"
            style={{
              backgroundImage: `url(${pn.refData?.previewPath || placeholder})`,
            }}
          ></div>

          <div className="ay-flex-1">
            {/* Product Title */}
            <h3 className="ay-font-bold ay-text-lg ay-mb-2">{productName}</h3>

            {/* Material Thumbnails */}
            <div className="ay-flex ay-flex-wrap ay-gap-2 ay-mb-3">
              {appliedRivestimento && (
                <MaterialThumbnail
                  material={appliedRivestimento}
                  label={labels.upholstery}
                />
              )}
              {appliedStruttura && (
                <MaterialThumbnail
                  material={appliedStruttura}
                  label={labels.structure}
                />
              )}
              {appliedFinitura && (
                <MaterialThumbnail
                  material={appliedFinitura}
                  label={labels.finish}
                />
              )}
            </div>

            {/* Controls */}
            <div className="ay-flex ay-flex-wrap ay-gap-2">
              {/* Rotation Control */}
              <div className="ay-flex ay-items-center ay-border ay-border-gray-200 ay-rounded-md ay-p-1 ay-bg-white">
                <label className="ay-mr-1 ay-text-gray-500">
                  <Md360 size={16} />
                </label>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    const currentAngle = pn.rotationAngle
                      ? Math.round((pn.rotationAngle * 180) / Math.PI)
                      : 0
                    const newAngle = (currentAngle - 5 + 360) % 360
                    rotateProductNodeInstance(pn)({
                      target: { value: newAngle },
                      preventDefault: () => {},
                      stopPropagation: () => {},
                    })
                  }}
                  className="ay-px-1 ay-text-sm ay-hover:ay-bg-gray-100 ay-rounded"
                >
                  ←
                </button>
                <input
                  type="number"
                  min="0"
                  max="360"
                  step="5"
                  className="ay-w-14 ay-h-6 ay-text-xs ay-border ay-border-gray-200 ay-rounded ay-text-center"
                  value={
                    pn.rotationAngle
                      ? Math.round((pn.rotationAngle * 180) / Math.PI)
                      : 0
                  }
                  onChange={(e) => {
                    const value = ((parseInt(e.target.value) % 360) + 360) % 360
                    rotateProductNodeInstance(pn)({
                      target: { value },
                      preventDefault: () => {},
                      stopPropagation: () => {},
                    })
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    const currentAngle = pn.rotationAngle
                      ? Math.round((pn.rotationAngle * 180) / Math.PI)
                      : 0
                    const newAngle = (currentAngle + 5) % 360
                    rotateProductNodeInstance(pn)({
                      target: { value: newAngle },
                      preventDefault: () => {},
                      stopPropagation: () => {},
                    })
                  }}
                  className="ay-px-1 ay-text-sm ay-hover:ay-bg-gray-100 ay-rounded"
                >
                  →
                </button>
              </div>

              {/* Height Control */}
              <div className="ay-flex ay-items-center ay-border ay-border-gray-200 ay-rounded-md ay-p-1 ay-bg-white">
                <label className="ay-mr-1 ay-text-gray-500">
                  <MdHeight size={16} />
                </label>
                <input
                  type="number"
                  min="-500"
                  max="500"
                  step="1"
                  value={pn.height ? pn.height * 100 : 100}
                  onChange={changeProductNodeInstanceHeight(pn)}
                  className="ay-w-14 ay-h-6 ay-text-xs ay-border ay-border-gray-200 ay-rounded ay-text-center"
                  onClick={(e) => e.stopPropagation()}
                />
                <span className="ay-text-xs ay-text-gray-500 ay-ml-1">%</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="ay-flex ay-flex-col ay-gap-2 ay-ml-2">
            <button
              onClick={duplicateProductNode(pn)}
              className="ay-text-blue-600 ay-rounded-full ay-w-8 ay-h-8 ay-flex ay-items-center ay-justify-center ay-hover:ay-bg-blue-50"
              title="Duplicate / Duplica"
            >
              <IoDuplicateSharp size={16} />
            </button>
            <button
              onClick={removeProductNodeInstance(pn)}
              className="ay-text-red-600 ay-rounded-full ay-w-8 ay-h-8 ay-flex ay-items-center ay-justify-center ay-hover:ay-bg-red-50"
              title="Remove / Rimuovi"
            >
              <IoClose size={18} />
            </button>
          </div>
        </div>

        {/* Materials toggle button */}
        <div className="ay-mt-3 ay-border-t ay-border-gray-200 ay-pt-2">
          <button
            onClick={() => setMaterialsOpen(!materialsOpen)}
            className="ay-text-sm ay-text-blue-600 ay-flex ay-items-center ay-justify-between ay-w-full"
          >
            <span>
              {materialsOpen
                ? "Nascondi materiali / Hide materials"
                : "Modifica materiali / Edit materials"}
            </span>
            {materialsOpen ? <FaChevronUp /> : <FaChevronDown />}
          </button>
        </div>
      </div>

      {/* Materials Accordion Section */}
      {materialsOpen && (
        <div className="ay-p-4 ay-border-t ay-border-gray-200 ay-bg-gray-50">
          {/* Material Tab Navigation - only show tabs for types with multiple options */}
          {availableMaterialTypes.length > 0 ? (
            <>
              <div className="ay-flex ay-mb-4 ay-border-b ay-border-gray-200">
                {availableMaterialTypes.map((type) => (
                  <button
                    key={type.id}
                    className={`ay-py-2 ay-px-4 ay-text-sm ${
                      activeTab === type.id
                        ? "ay-font-bold ay-border-b-2 ay-border-blue-500"
                        : ""
                    }`}
                    onClick={() => setActiveTab(type.id)}
                  >
                    {type.label}
                  </button>
                ))}
              </div>

              {/* Active Material Tab Content */}
              {activeTab === "imbottito" && hasMultipleOptions("imbottito") && (
                <MaterialTabContent
                  pn={pn}
                  materialType="imbottito"
                  applyMaterial={applyMaterial}
                  materialsList={materials}
                  matchedProduct={matchedProduct}
                  labels={labels}
                />
              )}

              {activeTab === "struttura" && hasMultipleOptions("struttura") && (
                <MaterialTabContent
                  pn={pn}
                  materialType="struttura"
                  applyMaterial={applyMaterial}
                  materialsList={wood}
                  matchedProduct={matchedProduct}
                  labels={labels}
                />
              )}

              {activeTab === "finitura" &&
                (hasMultipleOptions("piano") ||
                  hasMultipleOptions("finitura")) && (
                  <MaterialTabContent
                    pn={pn}
                    materialType="finitura"
                    applyMaterial={applyMaterial}
                    materialsList={wood}
                    matchedProduct={matchedProduct}
                    labels={labels}
                    customMaterials={matchedProduct.customPiano}
                  />
                )}
            </>
          ) : (
            <div className="ay-text-center ay-py-4 ay-text-gray-500">
              {labels.noConfigurableMaterials}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Component for a material tab content (Rivestimento, Struttura, Finitura)
 */
const MaterialTabContent = ({
  pn,
  materialType,
  applyMaterial,
  materialsList,
  matchedProduct,
  labels,
  customMaterials,
}) => {
  const [openCollections, setOpenCollections] = useState({})
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [filterText, setFilterText] = useState("")

  // Toggle collection open/closed
  const toggleCollection = (collection) => {
    setOpenCollections((prev) => ({
      ...prev,
      [collection]: !prev[collection],
    }))
  }

  // Get unique categories
  const getUniqueCategories = () => {
    if (!materialsList) return []
    return [...new Set(materialsList.map((m) => m.category))].filter(Boolean)
  }

  // Get configurable material with this name
  const getConfigurableMaterial = () => {
    return (
      pn.configurableMaterials?.find((m) => m.name === materialType) || null
    )
  }

  // Get available material variations from configurable material
  const getAvailableVariations = () => {
    const configMaterial = getConfigurableMaterial()
    if (!configMaterial || !configMaterial.materialVariations) return new Set()

    return new Set(configMaterial.materialVariations.map((v) => v.name))
  }

  // Filter materials by various criteria
  const getFilteredMaterials = () => {
    if (!materialsList) return []

    // Available variations from the product in Emersya
    const availableVariations = getAvailableVariations()

    // Start with all materials
    let filtered = materialsList

    // Get custom material filter
    let customMaterialFilter = null
    let customCollectionFilter = null

    if (materialType === "imbottito" && matchedProduct) {
      customMaterialFilter = matchedProduct.customMaterial
      customCollectionFilter = matchedProduct.customCollection
    } else if (materialType === "struttura" && matchedProduct) {
      customMaterialFilter = matchedProduct.customMaterial
    } else if (
      (materialType === "finitura" || materialType === "piano") &&
      matchedProduct
    ) {
      customMaterialFilter = matchedProduct.customPiano || customMaterials
    }

    // DEBUG: Log all available collections for comparison
    if (
      materialType === "imbottito" &&
      customCollectionFilter &&
      customCollectionFilter.length > 0
    ) {
      console.log("[DEBUG] Looking for collections:", customCollectionFilter)
      console.log(
        "[DEBUG] Available collections:",
        materialsList.map((m) => m.collection)
      )
    }

    // Helper function to normalize strings for comparison (handle case, accents, etc.)
    const normalizeForComparison = (str) => {
      if (!str) return ""
      // Convert to lowercase and remove accents
      return str
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
    }

    // FIRST PRIORITY: Filter by specific collections (only for fabrics)
    // If we have customCollection, ONLY show those collections
    if (
      customCollectionFilter &&
      Array.isArray(customCollectionFilter) &&
      customCollectionFilter.length > 0 &&
      materialType === "imbottito"
    ) {
      // Use case-insensitive and accent-insensitive comparison
      filtered = filtered.filter((m) => {
        const normalizedCollection = normalizeForComparison(m.collection)
        return customCollectionFilter.some(
          (cc) => normalizeForComparison(cc) === normalizedCollection
        )
      })

      // Log the result of filtering
      console.log(
        `[DEBUG] After collection filtering: ${filtered.length} collections found`
      )
      if (filtered.length > 0) {
        console.log(
          "[DEBUG] Matched collections:",
          filtered.map((m) => m.collection)
        )
      }
    }
    // SECOND PRIORITY: Only if not using customCollection, filter by category
    else if (selectedCategory && materialType === "imbottito") {
      filtered = filtered.filter((m) => m.category === selectedCategory)
    }

    // Filter materials based on custom filters and available variations
    filtered = filtered
      .map((m) => ({
        ...m,
        items: m.items.filter((item) => {
          // Skip items that aren't in available variations if we have a match in products.js
          // If no match in products.js, show all items (they'll be filtered by Emersya data)
          if (
            matchedProduct &&
            availableVariations.size > 0 &&
            !availableVariations.has(item.name)
          ) {
            return false
          }

          // Apply custom material filter if present
          if (
            customMaterialFilter &&
            Array.isArray(customMaterialFilter) &&
            customMaterialFilter.length > 0
          ) {
            return customMaterialFilter.some(
              (cm) =>
                normalizeForComparison(item.name) ===
                  normalizeForComparison(cm) ||
                normalizeForComparison(item.slug) === normalizeForComparison(cm)
            )
          }

          return true
        }),
      }))
      .filter((m) => m.items && m.items.length > 0)

    // Log for debug
    console.log(
      `[getFilteredMaterials] ${materialType}:`,
      filtered.length,
      "collections"
    )
    console.log(
      `[getFilteredMaterials] ${materialType} total items:`,
      filtered.reduce((acc, m) => acc + (m.items?.length || 0), 0)
    )

    // If we have 0 collections but we were expecting some based on customCollection,
    // try a more flexible matching approach as a fallback
    if (
      filtered.length === 0 &&
      customCollectionFilter &&
      customCollectionFilter.length > 0 &&
      materialType === "imbottito"
    ) {
      console.log("[DEBUG] Using fallback flexible collection matching")

      filtered = materialsList.filter((m) => {
        const normalizedCollection = normalizeForComparison(m.collection)
        return customCollectionFilter.some((cc) => {
          const normalizedCC = normalizeForComparison(cc)
          // Check if one contains the other
          return (
            normalizedCollection.includes(normalizedCC) ||
            normalizedCC.includes(normalizedCollection)
          )
        })
      })

      console.log(
        `[DEBUG] After flexible matching: ${filtered.length} collections found`
      )
    }

    // Handle the case where we should show all materials for products without customCollection
    // If there are no filtered collections but we have variations available, try to show all
    if (
      filtered.length === 0 &&
      !customCollectionFilter &&
      materialType === "imbottito"
    ) {
      console.log(
        "[DEBUG] No customCollection defined - showing all available materials"
      )
      // Return all materials, but still respect the available variations
      filtered = materialsList
        .map((m) => ({
          ...m,
          items: m.items.filter(
            (item) =>
              !availableVariations.size || availableVariations.has(item.name)
          ),
        }))
        .filter((m) => m.items && m.items.length > 0)

      console.log(
        `[DEBUG] All materials, filtered by variations: ${filtered.length} collections`
      )
    }

    return filtered
  }

  // Get search results
  const getSearchResults = () => {
    if (!filterText.trim() || !materialsList) return []

    const searchLower = filterText.toLowerCase()
    const availableVariations = getAvailableVariations()

    // Helper function to normalize strings for comparison (handle case, accents, etc.)
    const normalizeForComparison = (str) => {
      if (!str) return ""
      // Convert to lowercase and remove accents
      return str
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
    }

    // Filter considering customCollection (if present)
    let filteredList = materialsList

    // Apply customCollection filter if present
    if (
      matchedProduct &&
      matchedProduct.customCollection &&
      Array.isArray(matchedProduct.customCollection) &&
      matchedProduct.customCollection.length > 0 &&
      materialType === "imbottito"
    ) {
      filteredList = materialsList.filter((m) => {
        const normalizedCollection = normalizeForComparison(m.collection)
        return matchedProduct.customCollection.some(
          (cc) =>
            normalizeForComparison(cc) === normalizedCollection ||
            normalizedCollection.includes(normalizeForComparison(cc)) ||
            normalizeForComparison(cc).includes(normalizedCollection)
        )
      })
    }

    return filteredList
      .flatMap((m) => m.items)
      .filter((item) => {
        if (!item) return false

        // Skip items that aren't in available variations
        if (
          availableVariations.size > 0 &&
          !availableVariations.has(item.name)
        ) {
          return false
        }

        // Check if this item is allowed by custom filter
        let customMaterialFilter = null
        if (materialType === "imbottito") {
          customMaterialFilter = matchedProduct.customMaterial
        } else if (materialType === "struttura") {
          customMaterialFilter = matchedProduct.customMaterial
        } else if (materialType === "finitura") {
          customMaterialFilter = matchedProduct.customPiano || customMaterials
        }

        if (
          customMaterialFilter &&
          Array.isArray(customMaterialFilter) &&
          customMaterialFilter.length > 0
        ) {
          const matches = customMaterialFilter.some(
            (cm) =>
              normalizeForComparison(item.name) ===
                normalizeForComparison(cm) ||
              normalizeForComparison(item.slug) === normalizeForComparison(cm)
          )

          if (!matches) return false
        }

        return (
          (item.name &&
            normalizeForComparison(item.name).includes(
              normalizeForComparison(searchLower)
            )) ||
          (item.slug &&
            normalizeForComparison(item.slug).includes(
              normalizeForComparison(searchLower)
            ))
        )
      })
  }

  // Handle selecting a material
  const handleMaterialSelect = (item) => {
    if (!item || !item.name) return

    applyMaterial(pn, materialType, item.name)
  }

  const filteredMaterials = getFilteredMaterials()
  const searchResults = getSearchResults()

  // For debugging - log first collection and items
  if (filteredMaterials.length > 0) {
    console.log(
      `[MaterialTabContent] First collection for ${materialType}:`,
      filteredMaterials[0]
    )
  }

  // Show appropriate UI for this material type
  return (
    <div>
      {/* Category selector for Rivestimento - ONLY if no customCollection defined */}
      {materialType === "imbottito" &&
        (!matchedProduct.customCollection ||
          !matchedProduct.customCollection.length) && (
          <>
            <select
              value={selectedCategory || ""}
              onChange={(e) => setSelectedCategory(e.target.value || null)}
              className="ay-border ay-rounded-lg ay-border-gray-300 ay-w-full ay-p-2 ay-mb-3"
            >
              <option value="">{labels.allCategories}</option>
              {getUniqueCategories().map((category) => (
                <option key={category} value={category}>
                  {category.toUpperCase()}
                </option>
              ))}
            </select>

            {/* Search box for Rivestimento */}
            <input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder={labels.searchPlaceholder}
              className="ay-border ay-rounded-lg ay-border-gray-300 ay-w-full ay-p-2 ay-mb-4"
            />
          </>
        )}

      {/* Search results */}
      {searchResults.length > 0 && (
        <div className="ay-mb-4">
          <h5 className="ay-font-bold ay-mb-2">
            Risultati ricerca / Search results
          </h5>
          <div className="ay-grid ay-grid-cols-2 md:ay-grid-cols-3 lg:ay-grid-cols-4 ay-gap-4">
            {searchResults.map((item, i) => (
              <div
                key={`${item.slug || item.name}-${i}`}
                className="ay-group ay-relative ay-cursor-pointer"
                onClick={() => handleMaterialSelect(item)}
              >
                <div className="ay-overflow-hidden ay-rounded-lg ay-border ay-border-gray-200 ay-group-hover:ay-border-gray-900">
                  <img
                    className="ay-aspect-square ay-w-full ay-rounded-lg ay-object-cover ay-scale-125"
                    src={item.thumbnail || placeholder}
                    alt={item.name}
                  />
                </div>
                <p className="ay-text-sm ay-mt-2 ay-truncate">{item.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Material collections */}
      {filteredMaterials.length > 0 ? (
        filteredMaterials.map((collection, i) => {
          // Only continue if collection has items
          if (!collection.items || collection.items.length === 0) return null

          return (
            <div key={`${collection.collection}-${i}`} className="ay-mb-4">
              <div
                className="ay-cursor-pointer ay-border-b ay-border-gray-200 ay-py-1.5"
                onClick={() => toggleCollection(collection.collection)}
              >
                <h5 className="ay-font-bold ay-flex ay-justify-between">
                  {collection.collection.toUpperCase()}
                  <span>
                    {openCollections[collection.collection] ? "−" : "+"}
                  </span>
                </h5>
              </div>

              {openCollections[collection.collection] &&
                collection.items &&
                collection.items.length > 0 && (
                  <div className="ay-grid ay-grid-cols-2 md:ay-grid-cols-3 lg:ay-grid-cols-4 ay-gap-4 ay-mt-2">
                    {collection.items.map((item, i) => (
                      <div
                        key={`${item.slug || item.name}-${i}`}
                        className="ay-group ay-relative ay-cursor-pointer"
                        onClick={() => handleMaterialSelect(item)}
                      >
                        <div className="ay-overflow-hidden ay-rounded-lg ay-border ay-border-gray-200 ay-group-hover:ay-border-gray-900">
                          {item.thumbnail ? (
                            <img
                              className="ay-aspect-square ay-w-full ay-rounded-lg ay-object-cover ay-scale-125"
                              src={item.thumbnail || placeholder}
                              alt={item.name}
                            />
                          ) : (
                            <div
                              className="ay-aspect-square ay-w-full ay-rounded-lg ay-flex ay-items-center ay-justify-center"
                              style={{
                                backgroundColor: item.color || "#f0f0f0",
                              }}
                            >
                              {item.name}
                            </div>
                          )}
                        </div>
                        <p className="ay-text-sm ay-mt-2 ay-truncate">
                          {item.name}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          )
        })
      ) : (
        <div className="ay-text-gray-500 ay-text-center ay-py-4">
          {labels.noMaterialsAvailable}
        </div>
      )}

      {/* Direct display of material variations from Emersya if no matches found */}
      {filteredMaterials.length === 0 && materialType !== "imbottito" && (
        <DirectMaterialVariations
          pn={pn}
          materialType={materialType}
          applyMaterial={applyMaterial}
        />
      )}
    </div>
  )
}

/**
 * Component to fallback and display material variations directly from Emersya
 * when no matching local materials are found
 */
const DirectMaterialVariations = ({ pn, materialType, applyMaterial }) => {
  const [isExpanded, setIsExpanded] = useState(true)

  const configurableMaterial = pn.configurableMaterials?.find(
    (m) => m.name === materialType
  )

  if (
    !configurableMaterial ||
    !configurableMaterial.materialVariations?.length
  ) {
    return null
  }

  return (
    <div className="ay-mt-4">
      <div
        className="ay-cursor-pointer ay-border-b ay-border-gray-200 ay-py-1.5"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h5 className="ay-font-bold ay-flex ay-justify-between">
          {materialType.toUpperCase()}
          <span>{isExpanded ? "−" : "+"}</span>
        </h5>
      </div>

      {isExpanded && (
        <div className="ay-grid ay-grid-cols-2 md:ay-grid-cols-3 lg:ay-grid-cols-4 ay-gap-4 ay-mt-2">
          {configurableMaterial.materialVariations.map((variation, index) => (
            <div
              key={`${variation.name}-${index}`}
              className="ay-group ay-relative ay-cursor-pointer"
              onClick={() => applyMaterial(pn, materialType, variation.name)}
            >
              <div className="ay-overflow-hidden ay-rounded-lg ay-border ay-border-gray-200 ay-group-hover:ay-border-gray-900">
                {variation.color ? (
                  <div
                    className="ay-aspect-square ay-w-full ay-rounded-lg"
                    style={{ backgroundColor: variation.color }}
                  />
                ) : (
                  <div className="ay-aspect-square ay-w-full ay-rounded-lg ay-bg-gray-200 ay-flex ay-items-center ay-justify-center">
                    {variation.name}
                  </div>
                )}
              </div>
              <p className="ay-text-sm ay-mt-2 ay-truncate">{variation.name}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ProductItem
